import {
  ActionIcon,
  Badge,
  Box,
  Center,
  Collapse,
  Flex,
  Group,
  Image,
  Menu,
  Pill,
  rem,
  Stack,
  Text,
} from '@mantine/core';
import {
  CalendarIcon,
  CaretDownIcon,
  CaretUpIcon,
  DotsThreeVerticalIcon,
  ImageIcon,
  MapPinIcon,
  PenIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import type { FC, MouseEvent } from 'react';

import { capitalizeFirstLetter, emptyToUndefined } from '../../../helpers/stringHelpers.ts';
import { useI18n } from '../../../hooks/useI18n.ts';
import { formatDate } from '../../../lib/formatDate.ts';
import type { EventPanelStatus } from './EventPanel.tsx';

interface EventGroupPanelProps {
  status: EventPanelStatus;
  imageSrc: string | null;
  title?: string;
  tags: string[];
  firstDate: Date;
  lastDate: Date;
  eventCount: number;
  type?: 'live' | 'online' | 'secret';
  locationName?: string;
  events: {
    id: string;
    date: Date;
    status: EventPanelStatus;
  }[];
  onDropdownClick: (e: MouseEvent<HTMLButtonElement>, state: 'edit' | 'remove', id: string) => void;
  onDeleteAll?: (e: MouseEvent<HTMLButtonElement>, id: string) => void;
}

const statusToColor: Record<EventPanelStatus, { backgroundColor: string; dotColor: string }> = {
  draft: { dotColor: '#32CCDC', backgroundColor: 'blue.1' },
  pending: { dotColor: '#EE9F49', backgroundColor: '#FEEDDB' },
  published: { dotColor: '#80F130', backgroundColor: '#EDFBE7' },
  declined: { dotColor: '#E05252', backgroundColor: '#FDE8E8' },
  archived: { dotColor: '#9E9E9E', backgroundColor: '#F0F0F0' },
};

export const EventGroupPanel: FC<EventGroupPanelProps> = ({
  imageSrc,
  title,
  tags,
  firstDate,
  lastDate,
  eventCount,
  locationName,
  status,
  type,
  events,
  onDropdownClick,
  onDeleteAll,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useI18n();

  return (
    <Flex
      bg="white"
      c="gray.9"
      direction="column"
      h="100%"
      pb={0}
      pl="xxs"
      pos="relative"
      pr="xxs"
      pt="xxs"
      style={{ borderRadius: rem(16), overflow: 'hidden' }}
    >
      {/* Status badge */}
      <Box
        bg={statusToColor[status].backgroundColor}
        c="black"
        fw="500"
        h="32"
        left={16}
        pos="absolute"
        px="12"
        py="5"
        style={{ borderRadius: 'var(--mantine-radius-sm)' }}
        top={16}
      >
        <Flex align="center" gap="xxxs">
          <Box bg={statusToColor[status].dotColor} h="8" style={{ borderRadius: '100%' }} w="8" />
          <Text size="xxs">{t(`event-status--${status}`) ?? status}</Text>
        </Flex>
      </Box>

      {/* Recurring badge + menu */}
      <Flex align="center" gap="xxxs" pos="absolute" right={16} top={16}>
        <Box bg="#7C3AED" c="white" fw="500" h="32" px="12" py="5" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
          <Text size="xxs">{eventCount} events</Text>
        </Box>
        <Menu radius="xs" shadow="md">
          <Menu.Target>
            <ActionIcon
              color="gray.3"
              size="lg"
              variant="outline"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <DotsThreeVerticalIcon size={20} style={{ color: '#343330' }} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<TrashIcon size={22} />}
              onClick={(e) => {
                e.stopPropagation();
                if (onDeleteAll && events[0]) {
                  onDeleteAll(e, events[0].id);
                }
              }}
            >
              <Text size="xs">{t('event-panel_menu--remove-all') ?? 'Remove all'}</Text>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>

      {/* Image */}
      <Box bg="white" mt={32 + 16}>
        {imageSrc ? (
          <Image
            alt="event-image"
            crossOrigin="anonymous"
            radius="sm"
            src={imageSrc}
            style={{ aspectRatio: '4/5', objectFit: 'cover' }}
            w="100%"
          />
        ) : (
          <Box
            bg="gigi-outlines"
            p="md"
            style={{ aspectRatio: '4/5', borderRadius: 'var(--mantine-radius-md)' }}
            w="100%"
          >
            <Center h="100%">
              <ImageIcon color="#C0C0C0" size={72} />
            </Center>
          </Box>
        )}
      </Box>

      {/* Tags */}
      {tags.length > 0 && (
        <Group gap="xxxs" mt="sm">
          {tags.map((tag) => (
            <Pill key={tag}>{capitalizeFirstLetter(tag)}</Pill>
          ))}
        </Group>
      )}

      {/* Title */}
      <Text c="gray.9" fw={500} mt={12} size="md">
        {emptyToUndefined(title) ?? t('event-panel_message--no-title') ?? 'No title selected yet'}
      </Text>

      {/* Date range and location */}
      <Stack gap="xxxs" mb="sm" mt="xs">
        <Flex align="center" gap="xxxs">
          <CalendarIcon size={23} />
          <Text c="gray.6" fw={400} size="sm">
            {formatDate(firstDate)} – {formatDate(lastDate)}
          </Text>
        </Flex>

        <Flex align="center" gap="xxxs">
          <MapPinIcon size={23} />
          {type === 'secret' && <Badge size="xs">{t('event-panel_option--secret') ?? 'Secret'}</Badge>}
          {type === 'online' && <Badge size="xs">{t('event-panel_option--online') ?? 'Online'}</Badge>}
          {(type === 'live' || type === undefined) && (
            <Text c="gray.6" fw={400} size="sm">
              {emptyToUndefined(locationName) ?? t('event-panel_message--no-location') ?? 'Location not set'}
            </Text>
          )}
        </Flex>
      </Stack>

      {/* Expanded individual events */}
      <Collapse expanded={expanded}>
        <Stack gap="xxxs" mb="xs" mt="xs">
          {events.map((event) => (
            <Flex
              align="center"
              className="event-group-row"
              gap="xs"
              h={40}
              justify="space-between"
              key={event.id}
              px="xxxs"
              py="xxxs"
              style={{
                borderRadius: 'var(--mantine-radius-xs)',
                backgroundColor: 'var(--mantine-color-gray-0)',
                transition: 'background-color 150ms ease',
              }}
            >
              <Flex align="center" gap="xxs">
                <Box bg={statusToColor[event.status].dotColor} h="8" style={{ borderRadius: '100%' }} w="8" />
                <Text size="sm">{formatDate(event.date)}</Text>
              </Flex>
              <Flex gap="xxxs">
                <ActionIcon
                  color="gray"
                  radius="xs"
                  size="md"
                  variant="light"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDropdownClick(e, 'edit', event.id);
                  }}
                >
                  <PenIcon size={20} />
                </ActionIcon>
                <ActionIcon
                  color="red"
                  radius="xs"
                  size="md"
                  variant="light"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDropdownClick(e, 'remove', event.id);
                  }}
                >
                  <TrashIcon size={20} />
                </ActionIcon>
              </Flex>
            </Flex>
          ))}
        </Stack>
      </Collapse>

      {/* Recurring event banner - always last */}
      <Flex
        align="center"
        gap="xxs"
        justify="center"
        px="sm"
        py="xs"
        style={{
          cursor: 'pointer',
          backgroundColor: '#7C3AED',
          marginLeft: 'calc(-1 * var(--mantine-spacing-xxs))',
          marginRight: 'calc(-1 * var(--mantine-spacing-xxs))',
          marginTop: 'auto',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Text c="white" fw={600} size="xxs">
          Recurring event
        </Text>
        <Text c="white" fw={400} size="xxs">
          ·
        </Text>
        <Text c="white" fw={400} size="xxs" style={{ whiteSpace: 'nowrap' }}>
          {expanded ? 'Hide dates' : 'Show all dates'}
        </Text>
        {expanded ? <CaretUpIcon color="white" size={12} /> : <CaretDownIcon color="white" size={12} />}
      </Flex>
    </Flex>
  );
};
